import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Camera, Loader2 } from 'lucide-react';
import type { JobFileEntry } from '@jobos/shared';

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { JOB_PHOTO_THUMB_LIMIT, fetchJobPhotos, useJobPhotos } from '@/hooks/useJobPhotos';

// The recognition step the job list was missing.
//
// Filters get an estimator to a shortlist of comparable jobs; nothing on the row
// tells him what any of them *looked like*, so he opens each one to find out.
// This column answers that from the list: a count on every row, a grid of
// thumbnails under the pointer, the full viewer on a click. Sweeping the mouse
// down the column flashes each job's photos in turn, which is the whole point —
// far faster than open-modal / look / close-modal.
//
// Inline thumbnails were considered and rejected. They force a taller row, and
// normalising every row to that height fits fewer jobs on screen, which is
// exactly the scanning benefit this is meant to buy.

/**
 * How long the pointer must rest on a row before its popover opens.
 *
 * Without it a diagonal sweep across the column strobes every row it clips.
 */
const OPEN_DELAY_MS = 150;

/**
 * How long the popover survives the pointer leaving.
 *
 * This and `sideOffset={0}` are what make the thumbnails clickable at all:
 * with an instant close and a gap to cross, the pointer dismisses the popover
 * on its way into it.
 */
const CLOSE_DELAY_MS = 200;

interface JobPhotosCellProps {
  jobId: string;
  /** Absent on every single-job response — only the list carries it. */
  photoCount?: number;
  onOpenViewer: (photos: JobFileEntry[], index: number) => void;
}

export function JobPhotosCell({ jobId, photoCount, onOpenViewer }: JobPhotosCellProps) {
  const hasPhotos = !!photoCount && photoCount > 0;

  // Hover intent, raised on the same timer that opens the popover.
  //
  // It used to fire on mouse-enter at 0ms, so that a click landing inside the
  // 150ms open delay had a request already in flight to join. `fetchJobPhotos`
  // in openViewer covers exactly that click now, which leaves the eager fetch
  // buying 150ms at the price of a request per row the pointer merely clips —
  // and sweeping down the column is this feature's headline gesture, so a fast
  // pass over a 200-row list meant hundreds of uncancelled requests, two DB
  // round-trips each. One mechanism is enough; keep the cheaper one.
  const [intent, setIntent] = useState(false);
  const [open, setOpen] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data, isError } = useJobPhotos(jobId, { enabled: hasPhotos && intent });

  const clearTimers = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const handleEnter = useCallback(() => {
    clearTimeout(closeTimer.current);
    clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => {
      setIntent(true);
      setOpen(true);
    }, OPEN_DELAY_MS);
  }, []);

  const handleLeave = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, []);

  /**
   * Hand the viewer the whole set and the position within it.
   *
   * `index` indexes `photos`, never the six-thumbnail grid — they coincide for
   * the first six and diverge immediately after.
   *
   * A click can beat the hover's fetch, so this awaits it rather than doing
   * nothing: `fetchJobPhotos` joins the request already in flight (or starts
   * one, now that hover intent waits for the open delay), and a failure says so
   * instead of swallowing the click.
   *
   * An empty set says so too. The count on the row comes from the job list,
   * which caches at a 5min staleTime — delete a job's photos in another tab and
   * the row still reads 3 for minutes afterwards. Handing the lightbox `[]`
   * leaves it rendering nothing while the parent's viewer state stays set: a
   * click that silently does nothing, which is what spec §7 rules out.
   */
  const openViewer = useCallback(
    (index: number) => {
      clearTimers();
      setOpen(false);
      const hand = (photos: JobFileEntry[]) => {
        if (photos.length === 0) {
          toast({
            title: 'No photos to show',
            description: 'They may have been deleted since this list loaded.',
          });
          return;
        }
        onOpenViewer(photos, index);
      };
      if (data) {
        hand(data.photos);
        return;
      }
      setIsOpening(true);
      fetchJobPhotos(queryClient, jobId)
        .then((res) => hand(res.photos))
        .catch(() => {
          toast({
            title: 'Could not load photos',
            description: 'Open the job and try the Files tab.',
            variant: 'destructive',
          });
        })
        .finally(() => setIsOpening(false));
    },
    [clearTimers, data, jobId, onOpenViewer, queryClient, toast],
  );

  // Narrows photoCount to a number for the render below, as well as being the
  // empty-cell branch: 0 and undefined both mean "nothing to show".
  if (!photoCount) return null;

  const thumbs = (data?.photos ?? []).slice(0, JOB_PHOTO_THUMB_LIMIT);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Escape and outside-clicks come through here; the timers must not then
        // re-open a popover the user just dismissed.
        clearTimers();
        setOpen(next);
      }}
    >
      <PopoverAnchor asChild>
        <span
          className="inline-flex"
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            aria-label={`${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}`}
            onClick={() => openViewer(0)}
            className="inline-flex items-center gap-1 text-xs leading-none text-gray-500 hover:text-gray-800"
          >
            {isOpening ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Camera className="h-4 w-4" aria-hidden="true" />
            )}
            <span>{photoCount}</span>
          </button>
        </span>
      </PopoverAnchor>
      <PopoverContent
        side="right"
        align="start"
        // Zero gap, so the pointer never crosses dead space on its way in.
        sideOffset={0}
        className="w-64 p-2"
        // Radix pulls focus in on open and puts it back on close. During a mouse
        // sweep that yanks focus out of the table on every row crossed.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={(e) => e.stopPropagation()}
      >
        {isError ? (
          <p className="p-2 text-xs text-red-600">Could not load photos.</p>
        ) : !data ? (
          // Keyed off the answer not yet being here, rather than off the grid
          // being empty: a job whose count and photo set disagree would sit
          // under a skeleton that never resolves.
          <div data-testid="job-photos-skeleton" className="grid grid-cols-3 gap-1">
            {Array.from({ length: Math.min(photoCount, JOB_PHOTO_THUMB_LIMIT) }, (_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded bg-gray-200" />
            ))}
          </div>
        ) : thumbs.length === 0 ? (
          <p className="p-2 text-xs text-gray-500">No photos to show.</p>
        ) : (
          <div className="grid grid-cols-3 gap-1">
            {thumbs.map((photo, index) => {
              const url = data.thumbUrls[photo.id];
              if (!url) return null;
              return (
                <button
                  key={photo.id}
                  type="button"
                  onClick={() => openViewer(index)}
                  className="aspect-square overflow-hidden rounded bg-gray-100 hover:ring-2 hover:ring-blue-500"
                >
                  <img
                    src={url}
                    alt={photo.fileName}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
